import { Field, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { ClosedLostReason, closedLostReasonLabels } from '@/models/salesLead'

export interface ClosedLostDraft {
  reason: ClosedLostReason | null
  notes: string
}

export const EMPTY_CLOSED_LOST_DRAFT: ClosedLostDraft = { reason: null, notes: '' }

/** A picked reason, plus the note that "Other (see notes)" points to. */
export function isClosedLostDraftComplete(draft: ClosedLostDraft): boolean {
  if (draft.reason === null) return false
  return draft.reason !== ClosedLostReason.OTHER || draft.notes.trim() !== ''
}

interface ClosedLostReasonFieldsProps {
  /** Keeps the field ids unique when two of these render on one page. */
  idPrefix: string
  draft: ClosedLostDraft
  onChange: (draft: ClosedLostDraft) => void
}

/** The reason and note a move to Closed/Lost asks for. */
export function ClosedLostReasonFields({ idPrefix, draft, onChange }: ClosedLostReasonFieldsProps) {
  const reasonId = `${idPrefix}-closed-lost-reason`
  const notesId = `${idPrefix}-closed-lost-notes`
  const needsNote = draft.reason === ClosedLostReason.OTHER

  return (
    <FieldGroup>
      <Field>
        <FieldLabel htmlFor={reasonId}>Why was it lost?</FieldLabel>
        <Select
          value={draft.reason ?? ''}
          onValueChange={(reason) => onChange({ ...draft, reason: reason as ClosedLostReason })}
        >
          <SelectTrigger id={reasonId} className="w-full">
            <SelectValue placeholder="Pick a reason" />
          </SelectTrigger>
          <SelectContent>
            {Object.values(ClosedLostReason).map((reason) => (
              <SelectItem key={reason} value={reason}>
                {closedLostReasonLabels[reason]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <Field>
        <FieldLabel htmlFor={notesId}>{needsNote ? 'Notes' : 'Notes (optional)'}</FieldLabel>
        <Textarea
          id={notesId}
          value={draft.notes}
          onChange={(event) => onChange({ ...draft, notes: event.target.value })}
          placeholder={needsNote ? 'What happened?' : 'Anything the next rep should know'}
        />
      </Field>
    </FieldGroup>
  )
}
